import { SlideGroupsMap } from '@/features/_data/useUnitData';
import { StructureData } from '@/types/BaseSlide';
import { MagicModel } from '@/types/MagicModel';
import { SensorType } from '@/types/SensorType';
import { SlideGroupRecord, SlideGroupType } from '@/types/SlideGroupType';
import { FrontendSlide, FrontendStatus, StoredSlide } from '@/types/SlideType';
import { SlideVideoOptions } from '@/types/SlideVideoOptions';
import { TriggerId } from '@/types/TriggerId';
import { Unit } from '@/types/Unit';
import { booleanFilter } from '@/utils/arrayUtils';
import { fetcher } from '@/utils/fetcher';
import { deepEqual } from 'src/utils/deepEqual';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { createId } from './createId';

const cuid = createId();
export const getNewSlideSlideId = (slide: FrontendSlide) =>
  slide.slideId === undefined
    ? `${cuid}${slide.slideGroupId}${slide.index}`
    : slide.slideId;

const structureDataChanged = (
  structureDataOld: StructureData | undefined,
  structureDataNew: StructureData | undefined,
): boolean => {
  // checking if both triggerId arrays contain same values in O(n)
  if (typeof structureDataOld !== typeof structureDataNew) return true;
  const { triggerIds: triggerIdsOld = [] } = structureDataOld ?? {};
  const { triggerIds: triggerIdsNew = [] } = structureDataNew ?? {};
  if (triggerIdsOld.length !== triggerIdsNew.length) return true;
  const oldSet = new Set(triggerIdsOld);
  const newSet = new Set(triggerIdsNew);
  if (oldSet.size !== newSet.size) return true;
  // Do deep comparison instead of comparing references between items
  for (const item of newSet) {
    if (!Array.from(oldSet).some((oldItem) => deepEqual(oldItem, item)))
      return true;
  }
  return false;
};

const videoOptionsChanged = (
  videoOptionsOld: SlideVideoOptions | null | undefined,
  videoOptionsNew: SlideVideoOptions | null | undefined,
): boolean => {
  if (!videoOptionsNew) return false;
  if (typeof videoOptionsOld !== typeof videoOptionsNew) return true;
  if (videoOptionsOld && videoOptionsNew) {
    const { loop: loopOld, autoPlay: autoPlayOld } = videoOptionsOld;
    const { loop: loopNew, autoPlay: autoPlayNew } = videoOptionsNew;
    if (
      typeof loopOld !== typeof loopNew ||
      typeof autoPlayOld !== typeof autoPlayNew
    )
      return true;
    if (Boolean(loopOld) !== Boolean(loopNew)) return true;
    if (Boolean(autoPlayOld) !== Boolean(autoPlayNew)) return true;
  }
  return false;
};

export const getDiff = (
  oldData: SlideGroupsMap | null,
  newData: SlideGroupsMap | null,
  oldMagicModels?: MagicModel[],
  newMagicModels?: MagicModel[],
  oldChannelSelective?: boolean,
  newChannelSelective?: boolean,
  oldDisallowSlideDeletion?: boolean,
  newDisallowSlideDeletion?: boolean,
  oldLanguageSupport?: boolean,
  newLanguageSupport?: boolean,
  oldHomeTimer?: number,
  newHomeTimer?: number,
) => {
  const oldSlides = Object.fromEntries(
    Object.values(oldData ?? ({} as SlideGroupsMap)).flatMap((group) =>
      Object.values(group.slides).map((slide: FrontendSlide) => [
        getNewSlideSlideId(slide as FrontendSlide),
        slide,
      ]),
    ),
  );

  const newSlides = Object.fromEntries(
    Object.values(newData ?? ({} as SlideGroupsMap)).flatMap((group) =>
      Object.values(group.slides).map((slide: FrontendSlide) => [
        getNewSlideSlideId(slide as FrontendSlide),
        slide,
      ]),
    ),
  );

  const deletedSlides =
    oldData && newData
      ? Object.values(oldSlides).filter(
          (slide: FrontendSlide) => slide.slideId && !newSlides[slide.slideId],
        )
      : [];

  const createdSlides = Object.values(newSlides).filter(
    ({ slideId, __frontendInternalStatus }: FrontendSlide) =>
      __frontendInternalStatus === FrontendStatus.NEW ||
      (slideId && !oldSlides[slideId]),
  );

  const changedSlides = Object.values(newSlides).filter(
    (slide: FrontendSlide) => {
      const { slideId, videoOptions, structureData, __frontendInternalStatus } =
        slide;
      if (!slideId) return false;
      if (!oldSlides[slideId]) return false;

      const {
        structureData: oldStructuredData,
        videoOptions: oldVideoOptions = { autoPlay: true, loop: false },
      } = oldSlides[slideId];

      const structureDataHasChanged = structureDataChanged(
        oldStructuredData,
        structureData,
      );

      const videoOptionsHaveChanged = videoOptionsChanged(
        oldVideoOptions,
        videoOptions,
      );

      return (
        __frontendInternalStatus === FrontendStatus.MODIFIED ||
        __frontendInternalStatus === FrontendStatus.MODIFIED_STRUCTURE ||
        structureDataHasChanged ||
        videoOptionsHaveChanged
      );
    },
  );

  const changedOrderSlides = Object.values(oldSlides).filter(
    (slide: FrontendSlide) =>
      slide.slideId &&
      (newSlides?.[slide.slideId] as FrontendSlide)?.index !== slide.index,
  );

  const changedSlideGroups = Object.values(oldData ?? ({} as SlideGroupsMap))
    .map((group) => group as SlideGroupType)
    .filter(
      ({ id: slideGroupId, index, name }: SlideGroupType) =>
        index !== newData?.[slideGroupId]?.index ||
        name !== newData?.[slideGroupId].name,
    );

  const detectAddedSlideGroups = Object.values(
    newData ?? ({} as SlideGroupsMap),
  )
    .map((group) => group as SlideGroupType)
    .filter((newGroup) => {
      const oldGroup = oldData?.[newGroup.id];
      return (
        !oldGroup ||
        oldGroup.index !== newGroup.index ||
        oldGroup.name !== newGroup.name
      );
    });

  const isMagicModelListChanged =
    Array.isArray(oldMagicModels) &&
    (oldMagicModels?.length !== newMagicModels?.length ||
      !oldMagicModels?.every((oldMM) => {
        const isMMPresent = newMagicModels?.find(
          (newMM) => newMM.sensorId === oldMM.sensorId,
        );
        if (!isMMPresent) return false;

        return (
          isMMPresent.modelName === oldMM.modelName &&
          isMMPresent.sensorType === oldMM.sensorType
        );
      }));

  const changedChannel =
    // eslint-disable-next-line no-nested-ternary
    oldChannelSelective === undefined && newChannelSelective === true
      ? false
      : oldChannelSelective === undefined
        ? true
        : Boolean(oldChannelSelective) !== Boolean(newChannelSelective);

  const changedDisallowSlideDeletion =
    Boolean(newDisallowSlideDeletion) !== Boolean(oldDisallowSlideDeletion);

  const changedLanguageSupport =
    Boolean(newLanguageSupport) !== Boolean(oldLanguageSupport);

  const changedHomeTimer = newHomeTimer !== oldHomeTimer;

  console.debug(
    'getDiff [output]',
    deletedSlides,
    changedSlides,
    createdSlides,
    changedOrderSlides,
    changedSlideGroups,
    detectAddedSlideGroups,
    isMagicModelListChanged,
    changedChannel,
    changedDisallowSlideDeletion,
    changedLanguageSupport,
    changedHomeTimer,
  );

  return {
    deletedSlides,
    changedSlides,
    createdSlides,
    changedOrderSlides,
    changedSlideGroups,
    detectAddedSlideGroups,
    isMagicModelListChanged,
    changedChannel,
    changedDisallowSlideDeletion,
    changedLanguageSupport,
    changedHomeTimer,
  };
};

type CreateSlideResponse = {
  readonly id: string;
  readonly createdAt: {
    readonly _seconds: number;
    readonly _nanoseconds: number;
  };
  readonly changedAt: {
    readonly _seconds: number;
    readonly _nanoseconds: number;
  };
  readonly createdBy: string;
  readonly changedBy: string;
  readonly url: string;
  readonly readAccess: readonly unknown[];
  readonly writeAccess: readonly unknown[];
  readonly desc: string;
  readonly contentType: string;
  readonly archived: boolean;
  readonly name: string;
  readonly type: string;
  readonly origin: string;
  readonly ref: string;
  readonly size: number;
  readonly videoOptions: Readonly<SlideVideoOptions>;
  readonly splitScreen: boolean;
  readonly signedUploadUrl: string;
};

type UploadUnit = Omit<Unit, 'slides'> & {
  readonly slides: readonly FrontendSlide[];
};

export const createSlide = async (slide: FrontendSlide) => {
  const result = await fetcher<CreateSlideResponse>(
    `${RETAIL_API_URL}/slides/create`,
    {
      method: 'POST',
      body: JSON.stringify({
        id: slide.slideId,
        desc: ' ',
        customer: slide.customer,
        contentType: slide.contentType,
        archived: false,
        name: slide.name || slide.asset,
        // eslint-disable-next-line no-nested-ternary
        type: slide.contentType.includes('image')
          ? 'image'
          : slide.contentType.includes('video')
            ? 'video'
            : 'web.url',
        origin: 'retail',
        ref: slide.contentType === 'text/html' ? slide.asset : slide.name,
        size: slide.size || 0,
        videoOptions: slide?.videoOptions,
        splitScreen: slide?.splitScreen || false,
      }),
    },
  );
  return result;
};

export const buildUpdatePayload = async (
  unitData: Unit,
  newSlideGroups: SlideGroupRecord,
  createdSlides: FrontendSlide[],
  changedSlides: FrontendSlide[],
  librarySlides: StoredSlide[],
  newMagicModels: MagicModel[] | undefined,
  newChannel: boolean | undefined,
  disallowSlideDeletion: boolean | undefined,
  languageSupport: boolean | undefined,
  homeTimer: number | undefined,
): Promise<UploadUnit> => {
  const newSlidesState = Object.fromEntries(
    Object.values(newSlideGroups)
      .map(({ slides }) => Object.values(slides))
      .flat()
      .map((slide) => [getNewSlideSlideId(slide), slide]),
  );

  const newlyCreatedSlides = Object.fromEntries(
    await Promise.all(
      createdSlides
        .filter(
          (slide) =>
            slide.slideId &&
            !librarySlides
              .map((libraryslide) => libraryslide.id)
              .includes(slide.slideId),
        )
        .map(async (slide) => {
          const result = await createSlide(slide);
          return [
            getNewSlideSlideId(slide),
            { ...slide, createResponse: result },
          ] as const;
        }),
    ),
  );

  // upload files
  await Promise.all(
    Object.values(newlyCreatedSlides)
      .filter((newlyCreatedSlide) => newlyCreatedSlide.file)
      .map(async (newlyCreatedSlide) => {
        if (newlyCreatedSlide.file) {
          const result = await fetcher(
            newlyCreatedSlide.createResponse.signedUploadUrl,
            {
              method: 'PUT',
              body:
                typeof newlyCreatedSlide.file === 'string'
                  ? newlyCreatedSlide.file.split(',')[1]
                  : newlyCreatedSlide.file,
              headers: {
                'Content-Type': newlyCreatedSlide?.contentType,
              },
            },
          );

          return result;
        }

        return undefined;
      }),
  );

  // update modified slides name / videoOptions
  await Promise.all(
    changedSlides.map(async (slide) =>
      fetcher<CreateSlideResponse>(`${RETAIL_API_URL}/slides/update`, {
        method: 'POST',
        body: JSON.stringify({
          id: getNewSlideSlideId(slide),
          name: slide.name,
          videoOptions: {
            autoPlay: slide?.videoOptions?.autoPlay,
            loop: slide?.videoOptions?.loop,
          },
        }),
      }),
    ),
  );

  const finalSlides = Object.values(newSlidesState).map((slide) => {
    return {
      id: slide.id,
      index: slide.index,
      slideGroupId: slide.slideGroupId,
      slideId:
        newlyCreatedSlides[getNewSlideSlideId(slide)]?.createResponse.id ??
        slide.slideId,
      videoOptions: slide?.videoOptions,
      name: slide?.name,
      contentType: slide?.contentType,
    };
  });

  const finalSlideGroups = Object.values(newSlideGroups).map((slideGroup) => {
    const sanitizedSlides: Record<string, FrontendSlide> = {};
    Object.keys(slideGroup.slides).forEach((key: string) => {
      const sanitizedSlide = slideGroup.slides[key];
      delete sanitizedSlide.asset;
      sanitizedSlides[key] = sanitizedSlide;
    });
    return {
      id: slideGroup.id,
      index: slideGroup.index,
      name: slideGroup.name,
      slides: sanitizedSlides,
    };
  });

  const payload: UploadUnit = {
    ...unitData,
    nfcTagIdList: newMagicModels || unitData.nfcTagIdList,
    selectedMappingTag:
      newChannel === undefined ? unitData.selectedMappingTag : newChannel,
    slideDeleteDisallowed:
      disallowSlideDeletion === undefined
        ? unitData.slideDeleteDisallowed
        : disallowSlideDeletion,
    languageSupport: languageSupport ?? unitData.languageSupport,
    homeTimer: homeTimer ?? unitData.homeTimer,
    complexityType: 'complex',
    slideGroups: finalSlideGroups,
    slides: finalSlides,
    structure: {
      navigationMode: 'retail',
      children: Object.values(newSlideGroups)
        .map((slideGroup): StructureData => {
          const firstSlide = Object.values(slideGroup.slides)[0];

          const transformTriggerIds = (
            triggerIds: readonly TriggerId[] | undefined,
          ) =>
            triggerIds?.map((triggerId) => ({
              ...triggerId,
              values: triggerId.values.map((value) => {
                if (
                  [SensorType.GENERIC, SensorType.TOUCH].includes(
                    triggerId.type,
                  )
                ) {
                  try {
                    return JSON.parse(value);
                  } catch (err) {
                    // do nothing
                  }

                  return value;
                }

                return value;
              }),
            })) ?? [];

          const structureChildren = Object.values(slideGroup.slides)
            .slice(1)
            .map(({ slideId, structureData: slideStructureData }) => ({
              ...slideStructureData,
              id:
                (slideStructureData &&
                  newlyCreatedSlides[slideStructureData.id]?.createResponse
                    .id) ??
                slideStructureData?.id ??
                createId(),
              children: [],
              initialState: slideStructureData?.initialState ?? null,
              slideGroupId: slideGroup.id,
              slideIds: [slideId],
              triggerIds: transformTriggerIds(slideStructureData?.triggerIds),
              sensorIds: firstSlide?.structureData?.sensorIds ?? [],
            }))
            .filter(booleanFilter);

          return {
            id: firstSlide?.structureData?.id || createId(),
            slideGroupId: slideGroup.id,
            initialState: firstSlide?.structureData?.initialState || null,
            sensorIds: firstSlide?.structureData?.sensorIds ?? [],
            triggerIds: transformTriggerIds(
              firstSlide?.structureData?.triggerIds,
            ),
            children: structureChildren,
            slideIds: firstSlide?.slideId ? [firstSlide.slideId] : [],
          };
        })
        .filter(booleanFilter),
    },
  };

  return payload;
};
